/*
  Karnataka Gramin Bank QR card for the NEW mold (KAGBNewQRCard) - printed at 84.3mm x 133mm.
  Same elements as the old-mold card (KAGBQRCard), re-proportioned for the shorter
  new-mold page: smaller type, a tighter QR box, and the call-centre / WhatsApp
  lines laid out with the icon beside the text instead of above it.
  Sizes are tuned for the 385 x 607 px capture BulkQRDialog renders this card at.
*/
import React, { useRef } from 'react';
import { Box, Typography } from '@mui/material';
import QRCodeGenerator from './QRCodeGenerator';
import FitMerchantName from './FitMerchantName';

const BRAND_BLUE = '#2858A8';
const BRAND_GREEN = '#439F46';
const LEAF_PATTERN = '/assets/KAGB/kagb_bg.png';

const CALL_CENTRE_NUMBERS = '18001025250 / 18001038210';
const WHATSAPP_NUMBER = '8886646362';

export default function KagbNewScanAndPayCard({ qrString, merchantVpa, merchantName }) {
    const cardRef = useRef(null);

    return (
        <Box
            ref={cardRef}
            sx={{
                width: '100%',
                height: '100%',
                borderRadius: '36px',
                background: `linear-gradient(180deg, ${BRAND_BLUE} 0%, ${BRAND_BLUE} 30%, #3B7B7F 70%, ${BRAND_GREEN} 100%)`,
                boxShadow: 3,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                px: 1.25,
                pt: 1.75,
                pb: 1.25,
                boxSizing: 'border-box'
            }}
        >
            {/* The leaf pattern is a repeating CSS background, which html2canvas can
          snapshot before the tile has loaded. This hidden <img> makes the
          bulk-PDF image wait cover it — by the time the capture runs the tile
          is in cache and the background paints. */}
            <Box
                component="img"
                src={LEAF_PATTERN}
                alt=""
                aria-hidden="true"
                sx={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
            />

            {/* Bank logo on the blue header */}
            <Box
                component="img"
                src="/assets/KAGB/kagblogo.svg"
                alt="Karnataka Grameena Bank"
                sx={{ width: '90%', maxWidth: '100%', height: 'auto', objectFit: 'contain', flexShrink: 0 }}
            />

            {/* White panel */}
            <Box
                sx={{
                    position: 'relative',
                    mt: 1.5,
                    width: '100%',
                    flex: 1,
                    minHeight: 0,
                    minWidth: 0,
                    backgroundColor: '#ffffff',
                    borderRadius: '28px',
                    px: 1.5,
                    pt: 1,
                    pb: 1.75,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    textAlign: 'center',
                    overflow: 'hidden',
                    boxSizing: 'border-box'
                }}
            >
                {/* Watermark leaves */}
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        backgroundImage: `url(${LEAF_PATTERN})`,
                        backgroundRepeat: 'repeat',
                        backgroundSize: '46px auto',
                        opacity: 0.5,
                        pointerEvents: 'none'
                    }}
                />

                {/* Heading - tight line-height so the heading sits close to the
            top of the panel instead of carrying Montserrat's leading. */}
                <Box sx={{ position: 'relative', flexShrink: 0 }}>
                    <Typography
                        sx={{
                            color: BRAND_BLUE,
                            fontFamily: 'Montserrat',
                            fontWeight: 600,
                            fontSize: 26,
                            lineHeight: 1.05
                        }}
                    >
                        Scan &amp; Pay
                    </Typography>
                    <Typography
                        sx={{
                            color: BRAND_BLUE,
                            fontFamily: 'Montserrat',
                            fontWeight: 500,
                            fontSize: 13,
                            lineHeight: 1.4,
                            mt: 0.5
                        }}
                    >
                        Scan QR via any UPI app
                    </Typography>
                </Box>

                {/* QR box — sized off the card width so it scales with the card. */}
                <Box
                    sx={{
                        position: 'relative',
                        border: '2px solid #000000',
                        borderRadius: '16px',
                        backgroundColor: '#ffffff',
                        width: '74%',
                        p: 1.5,
                        mt: 1,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        boxSizing: 'border-box',
                        flexShrink: 0,
                        '& svg': { width: '100%', height: 'auto', display: 'block' }
                    }}
                >
                    <QRCodeGenerator qrString={qrString} size={280} />
                </Box>

                {/* UPI ID + merchant name */}
                <Box sx={{ position: 'relative', width: '100%', flexShrink: 0, mt: 1 }}>
                    <Typography
                        sx={{
                            color: '#000',
                            fontFamily: 'Montserrat',
                            fontWeight: 500,
                            fontSize: 13,
                            lineHeight: 1.3,
                            px: 0.5,
                            overflowWrap: 'anywhere',
                            wordBreak: 'break-all'
                        }}
                    >
                        UPI ID : {merchantVpa}
                    </Typography>
                    <FitMerchantName
                        text={merchantName}
                        maxFontSize={18}
                        minFontSize={12}
                        maxLines={2}
                        lineHeight={1.2}
                        fontWeight={700}
                        containerSx={{ px: 0.5, width: '100%' }}
                    />
                </Box>

                {/* Call centre — icon sits beside the text on the new mold */}
                <Box
                    sx={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        maxWidth: '100%',
                        mt: 1,
                        flexShrink: 0
                    }}
                >
                    <Box
                        component="img"
                        src="/assets/KAGB/call.svg"
                        alt="Call"
                        sx={{ width: 24, height: 24, objectFit: 'contain', flexShrink: 0 }}
                    />
                    <Typography
                        sx={{
                            color: '#000',
                            fontFamily: 'Montserrat',
                            fontWeight: 500,
                            fontSize: 12,
                            lineHeight: 1.35,
                            textAlign: 'center'
                        }}
                    >
                        KGB call center services :
                        <br />
                        <Box component="span" sx={{ fontWeight: 700 }}>
                            {CALL_CENTRE_NUMBERS}
                        </Box>
                    </Typography>
                </Box>

                {/* WhatsApp banking */}
                <Box
                    sx={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        maxWidth: '100%',
                        mt: 0.75,
                        flexShrink: 0
                    }}
                >
                    <Box
                        component="img"
                        src="/assets/KAGB/whatsapp.svg"
                        alt="WhatsApp"
                        sx={{ width: 24, height: 24, objectFit: 'contain', flexShrink: 0 }}
                    />
                    <Typography
                        sx={{
                            color: '#000',
                            fontFamily: 'Montserrat',
                            fontWeight: 500,
                            fontSize: 12,
                            lineHeight: 1.35,
                            textAlign: 'center',
                            maxWidth: 215
                        }}
                    >
                        Say <Box component="span" sx={{ fontWeight: 700 }}>&ldquo;Hi&rdquo;</Box> to{' '}
                        <Box component="span" sx={{ fontWeight: 700 }}>{WHATSAPP_NUMBER}</Box> to avail Whatsapp
                        Banking Services
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}
